import { useEffect, useRef, useState } from 'react';
import {
  whatsappConnect, whatsappDisconnect, whatsappStatus,
} from '../lib/api';

// WhatsApp logo (verde quando connesso, grigio altrimenti)
function WhatsAppIcon({ size = 18, color }) {
  return (
    <svg viewBox="0 0 32 32" width={size} height={size} aria-hidden="true">
      <path fill={color}
        d="M19.11 17.205c-.372 0-1.088 1.39-1.518 1.39-.094 0-.187-.044-.27-.087-.625-.355-1.215-.764-1.755-1.215-.566-.456-1.083-.987-1.495-1.594-.044-.084-.087-.176-.087-.27 0-.43 1.39-1.146 1.39-1.519 0-.176-.142-.41-.252-.583-.142-.226-.328-.518-.5-.78l-.515-.79c-.05-.083-.124-.166-.215-.166-.143 0-.276.038-.43.094-.156.06-.473.176-.732.32-.346.196-.65.43-.85.79-.196.353-.314.792-.314 1.197 0 .398.105.79.246 1.157.295.788.713 1.51 1.246 2.166.78.96 1.733 1.79 2.798 2.45.46.288.96.527 1.476.704.46.158.94.272 1.43.272.295 0 .59-.057.873-.18.296-.13.587-.343.85-.59.21-.196.367-.43.367-.7 0-.27-.094-.55-.27-.78-.165-.213-.4-.4-.65-.49-.245-.084-.5-.124-.756-.124zM16.01 4.998c-6.082 0-11.013 4.93-11.013 11.012 0 1.962.518 3.81 1.42 5.41L4.998 27.002l5.78-1.45c1.555.853 3.34 1.342 5.232 1.342 6.082 0 11.012-4.93 11.012-11.012 0-6.082-4.93-11.012-11.012-11.012zm0 20.116c-1.715 0-3.314-.49-4.667-1.318l-3.255.823.83-3.18c-.946-1.42-1.5-3.124-1.5-4.943 0-4.965 4.027-8.993 8.992-8.993s8.992 4.028 8.992 8.993-4.027 8.993-8.992 8.993z" />
    </svg>
  );
}

function statusColor(state) {
  switch (state) {
    case 'connected': return '#22c55e';
    case 'qr':
    case 'connecting': return '#eab308';
    case 'service_down': return '#dc2626';
    default: return '#94a3b8';
  }
}

function statusLabel(state) {
  return ({
    connected: 'Connesso',
    connecting: 'Connessione…',
    qr: 'In attesa di scansione QR',
    disconnected: 'Non connesso',
    logged_out: 'Disconnesso',
    service_down: 'Servizio non disponibile',
  })[state] || state;
}

export default function WhatsAppButton() {
  const [status, setStatus] = useState({ state: 'disconnected' });
  const [open, setOpen] = useState(false);
  const [connecting, setConnecting] = useState(false);
  const pollingRef = useRef(null);

  // Status iniziale + polling lento (ogni 30s) quando non aperto
  useEffect(() => {
    let alive = true;
    const tick = async () => {
      try {
        const s = await whatsappStatus();
        if (alive) setStatus(s);
      } catch { /* ignore */ }
    };
    tick();
    const interval = setInterval(tick, 30000);
    return () => { alive = false; clearInterval(interval); };
  }, []);

  // Polling veloce (ogni 2s) quando il modale è aperto e siamo in stato qr/connecting
  useEffect(() => {
    if (!open) {
      if (pollingRef.current) clearInterval(pollingRef.current);
      pollingRef.current = null;
      return;
    }
    const tick = async () => {
      try {
        const s = await whatsappStatus();
        setStatus(s);
        if (s.state === 'connected') {
          // resta aperto qualche secondo per mostrare il successo, poi chiudi auto
          setTimeout(() => setOpen(false), 2500);
        }
      } catch { /* ignore */ }
    };
    tick();
    pollingRef.current = setInterval(tick, 2000);
    return () => {
      if (pollingRef.current) clearInterval(pollingRef.current);
      pollingRef.current = null;
    };
  }, [open]);

  const handleConnect = async () => {
    setConnecting(true);
    try {
      const s = await whatsappConnect();
      setStatus(s);
    } catch (e) {
      console.error(e);
    } finally {
      setConnecting(false);
    }
  };

  const handleDisconnect = async () => {
    if (!window.confirm('Disconnettere WhatsApp e cancellare la sessione?')) return;
    try {
      await whatsappDisconnect();
      setStatus({ state: 'disconnected' });
    } catch (e) {
      window.alert('Errore disconnessione');
    }
  };

  const color = statusColor(status.state);

  return (
    <>
      <button
        type="button"
        className="iconbtn"
        onClick={() => setOpen(true)}
        aria-label="WhatsApp"
        title={`WhatsApp: ${statusLabel(status.state)}`}
        style={{ position: 'relative' }}
      >
        <WhatsAppIcon size={18} color={status.state === 'connected' ? '#16a34a' : 'currentColor'} />
        <span style={{
          position: 'absolute',
          bottom: 4,
          right: 4,
          width: 8,
          height: 8,
          borderRadius: '50%',
          background: color,
          border: '1.5px solid var(--bg)',
        }} />
      </button>

      {open && (
        <div
          onClick={() => setOpen(false)}
          style={{
            position: 'fixed', inset: 0, zIndex: 2000,
            background: 'oklch(0 0 0 / 0.5)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            padding: 20,
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              background: 'var(--bg-elev)',
              borderRadius: 12,
              padding: 24,
              width: 'min(440px, 100%)',
              boxShadow: '0 10px 40px oklch(0 0 0 / 0.3)',
            }}
          >
            <header style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20 }}>
              <WhatsAppIcon size={24} color="#16a34a" />
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 16, fontWeight: 600 }}>WhatsApp</div>
                <div style={{ fontSize: 12.5, color: 'var(--fg-muted)' }}>
                  Connessione per l'organizzazione
                </div>
              </div>
              <button type="button" onClick={() => setOpen(false)} className="iconbtn" aria-label="Chiudi">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                     strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              </button>
            </header>

            <div style={{ marginBottom: 16, display: 'flex', alignItems: 'center', gap: 8 }}>
              <span style={{
                width: 8, height: 8, borderRadius: '50%', background: color,
              }} />
              <span style={{ fontSize: 13, fontWeight: 500 }}>{statusLabel(status.state)}</span>
              {status.phone && (
                <span style={{ marginLeft: 'auto', fontSize: 12, color: 'var(--fg-muted)' }}>
                  +{status.phone}
                </span>
              )}
            </div>

            {/* CONNECTED */}
            {status.state === 'connected' && (
              <div>
                <div style={{
                  background: 'oklch(0.96 0.06 150)',
                  border: '1px solid oklch(0.85 0.10 150)',
                  borderRadius: 8,
                  padding: 16,
                  marginBottom: 16,
                }}>
                  <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 4 }}>
                    ✓ Connesso come {status.name || 'WhatsApp'}
                  </div>
                  <div style={{ fontSize: 12.5, color: 'var(--fg-muted)' }}>
                    +{status.phone}{status.connected_at && ` · dal ${new Date(status.connected_at).toLocaleString('it-IT')}`}
                  </div>
                </div>
                <button type="button" className="btn btn--outline" onClick={handleDisconnect} style={{ width: '100%', justifyContent: 'center' }}>
                  Disconnetti WhatsApp
                </button>
              </div>
            )}

            {/* QR CODE */}
            {status.state === 'qr' && status.qr_data_url && (
              <div>
                <div style={{ fontSize: 13.5, color: 'var(--fg-muted)', marginBottom: 12, lineHeight: 1.5 }}>
                  1. Apri <strong>WhatsApp</strong> sul telefono<br />
                  2. Vai su <strong>Impostazioni → Dispositivi collegati → Collega dispositivo</strong><br />
                  3. Inquadra il QR qui sotto
                </div>
                <div style={{
                  display: 'flex', justifyContent: 'center',
                  padding: 12, background: 'white', borderRadius: 8,
                  border: '1px solid var(--border)',
                }}>
                  <img
                    src={status.qr_data_url}
                    alt="QR WhatsApp"
                    style={{ width: 280, height: 280, imageRendering: 'pixelated' }}
                  />
                </div>
                <div style={{ fontSize: 11, color: 'var(--fg-subtle)', marginTop: 8, textAlign: 'center' }}>
                  Il QR si aggiorna automaticamente. Non chiudere questa finestra.
                </div>
              </div>
            )}

            {/* CONNECTING (no QR yet) */}
            {status.state === 'connecting' && !status.qr_data_url && (
              <div style={{ textAlign: 'center', padding: 40, color: 'var(--fg-muted)' }}>
                <div style={{ marginBottom: 12 }}>Connessione al servizio WhatsApp…</div>
                <div style={{ fontSize: 12 }}>Generazione del QR code in corso.</div>
              </div>
            )}

            {/* DISCONNECTED / LOGGED_OUT */}
            {(status.state === 'disconnected' || status.state === 'logged_out') && (
              <div>
                <p style={{ fontSize: 13, color: 'var(--fg-muted)', marginBottom: 16, lineHeight: 1.5 }}>
                  Collega un account WhatsApp alla tua organizzazione per inviare messaggi
                  ai lead direttamente dal gestionale.
                </p>
                <button
                  type="button"
                  className="btn btn--solid"
                  onClick={handleConnect}
                  disabled={connecting}
                  style={{ width: '100%', justifyContent: 'center' }}
                >
                  {connecting ? 'Avvio…' : 'Connetti WhatsApp'}
                </button>
              </div>
            )}

            {/* SERVICE DOWN */}
            {status.state === 'service_down' && (
              <div style={{
                background: 'oklch(0.96 0.04 25)',
                border: '1px solid oklch(0.85 0.10 25)',
                borderRadius: 8,
                padding: 14,
                fontSize: 13,
                color: 'oklch(0.35 0.15 25)',
              }}>
                Il servizio WhatsApp non è raggiungibile. Contatta l'amministratore.
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
}
